/**
 * CloseModal — Cierre clínico de teleconsulta (3 pasos)
 *
 * Inspirado en patrones de Doximity, Doxy.me y Teladoc:
 *   1. Resultado clínico  → disposición, dx principal, plan de seguimiento, recetas/lab/educación
 *   2. Calidad técnica   → conexión + IA (opcional, no bloqueante)
 *   3. Firmar y finalizar → preview SOAP + comentario libre + firma
 *
 * El payload se pasa a onConfirm. La persistencia (Cloud Function) la maneja
 * el llamador (VideoCallPageV2 → portal_finalizarConsulta).
 */

import { useState, useMemo, useEffect } from 'react';
import {
  PhoneOff, Stethoscope, CalendarClock, Pill, Microscope, BookOpen,
  Brain, FileSignature, ChevronLeft, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import type { DisposicionConsulta } from '../../types';
import styles from './CloseModal.module.css';

export type PlanSeguimiento =
  | 'ninguno'
  | '1_semana'
  | '2_semanas'
  | '1_mes'
  | 'especialista'
  | 'urgencias';

export interface CloseModalConfirmData {
  // Resultado clínico
  disposicion: DisposicionConsulta;
  diagnosticoPrincipal?: { codigo: string; descripcion: string };
  planSeguimiento: PlanSeguimiento;
  recetaEmitida: boolean;
  ordenLabEmitida: boolean;
  educacionEntregada: boolean;

  // Calidad (opcional)
  calidadConexion?: number;       // 1-5
  calidadIA?: number;             // 1-5
  tagsFeedback: string[];

  // Firma
  comentarioMedico: string;

  // Compatibilidad con el contrato anterior — opcional ahora que se
  // eliminó el paso de calidad. Si se envía rating, debe estar en 1-5.
  rating?: number;                // = calidadIA (legacy, opcional)
  feedback: string;               // = comentarioMedico (legacy)
  tags: string[];                 // = tagsFeedback (legacy)
}

export interface CloseModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: CloseModalConfirmData) => void;
  patientName: string;
  duration: string;
  loading?: boolean;
  // SOAP opcional para preview en el paso 3
  soapPreview?: { s?: string; o?: string; a?: string; p?: string };
  fhirCount?: number;
  alertCount?: number;
  /** Disposición preseleccionada al abrir (ej. desde el banner "no contestó"). */
  disposicionInicial?: DisposicionConsulta;
}

const DISPOSITION_OPTIONS: { value: DisposicionConsulta; label: string; description: string }[] = [
  { value: 'completada',           label: 'Completada',         description: 'Consulta finalizó con éxito' },
  { value: 'referida',             label: 'Referida',           description: 'Derivada a especialista' },
  { value: 'incompleta',           label: 'Incompleta',         description: 'Faltó información o evaluación' },
  { value: 'paciente_no_presento', label: 'No se presentó',     description: 'Paciente nunca conectó' },
  { value: 'paciente_rechazo',     label: 'Rechazó la llamada', description: 'El paciente rechazó la videollamada' },
  { value: 'fallo_tecnico',        label: 'Fallo técnico',      description: 'Problema audio/video' },
  { value: 'cancelada_medico',     label: 'Cancelada',          description: 'Cancelada por el médico' },
];

const PLAN_OPTIONS: { value: PlanSeguimiento; label: string; sub: string }[] = [
  { value: 'ninguno',      label: 'Sin seguimiento',  sub: 'Caso cerrado' },
  { value: '1_semana',     label: '1 semana',         sub: 'Revisión a 7 días' },
  { value: '2_semanas',    label: '2 semanas',        sub: 'Revisión a 14 días' },
  { value: '1_mes',        label: '1 mes',            sub: 'Revisión a 30 días' },
  { value: 'especialista', label: 'Especialista',     sub: 'Referencia externa' },
  { value: 'urgencias',    label: 'Urgencias',        sub: 'Atención inmediata' },
];

type Step = 'outcome' | 'sign';

export function CloseModal({
  open,
  onClose,
  onConfirm,
  patientName,
  duration,
  loading,
  soapPreview,
  fhirCount = 0,
  alertCount = 0,
  disposicionInicial,
}: CloseModalProps) {
  const [step, setStep] = useState<Step>('outcome');

  // Step 1: Outcome
  const [disposicion, setDisposicion] = useState<DisposicionConsulta>('completada');

  // Al ABRIR, aplicar la disposición preseleccionada (ej. el banner de
  // "no contestó / rechazó" abre el modal con la opción correcta marcada).
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisposicion(disposicionInicial ?? 'completada');
      setStep('outcome');
    }
  }, [open, disposicionInicial]);
  const [planSeguimiento, setPlanSeguimiento] = useState<PlanSeguimiento>('ninguno');
  const [recetaEmitida, setRecetaEmitida] = useState(false);
  const [ordenLabEmitida, setOrdenLabEmitida] = useState(false);
  const [educacionEntregada, setEducacionEntregada] = useState(false);
  const [dxCodigo, setDxCodigo] = useState('');
  const [dxDescripcion, setDxDescripcion] = useState('');

  // Step 2: Sign
  const [comentarioMedico, setComentarioMedico] = useState('');

  const hasSOAP = useMemo(
    () => !!(soapPreview?.s || soapPreview?.o || soapPreview?.a || soapPreview?.p),
    [soapPreview],
  );

  const handleConfirm = () => {
    const diagnosticoPrincipal = dxCodigo.trim() || dxDescripcion.trim()
      ? { codigo: dxCodigo.trim(), descripcion: dxDescripcion.trim() }
      : undefined;

    onConfirm({
      disposicion,
      diagnosticoPrincipal,
      planSeguimiento,
      recetaEmitida,
      ordenLabEmitida,
      educacionEntregada,
      // Calidad ya no se recolecta — enviar undefined (backend valida 1-5
      // y rechaza 0; undefined hace que el backend salte la validación).
      tagsFeedback: [],
      comentarioMedico,
      // Legacy alias — NO enviar rating:0, el backend lo rechazaría
      feedback: comentarioMedico,
      tags: [],
    });
  };

  const dispositionLabel =
    DISPOSITION_OPTIONS.find((d) => d.value === disposicion)?.label ?? disposicion;

  return (
    <Modal open={open} onClose={onClose} title="Finalizar consulta" size="lg">
      <div className={styles.content}>
        {/* Stepper — 2 pasos */}
        <div className={styles.stepper} role="tablist" aria-label="Pasos de cierre">
          {(['outcome', 'sign'] as const).map((s, idx) => {
            const labels = ['Resultado', 'Firmar'];
            const active = step === s;
            const done = s === 'outcome' && step === 'sign';
            return (
              <div
                key={s}
                className={`${styles.stepperItem} ${active ? styles.stepperItemActive : ''} ${done ? styles.stepperItemDone : ''}`}
                role="tab"
                aria-selected={active}
              >
                <span className={styles.stepperDot}>{done ? <CheckCircle2 size={14} aria-hidden="true" /> : idx + 1}</span>
                <span className={styles.stepperLabel}>{labels[idx]}</span>
              </div>
            );
          })}
        </div>

        {/* ── Step 1: Outcome ─────────────────────────────────────────── */}
        {step === 'outcome' && (
          <>
            <div className={styles.iconWrap}>
              <div className={styles.iconCircle}>
                <Stethoscope size={24} aria-hidden="true" />
              </div>
            </div>

            <h3 className={styles.heading}>Resultado de la consulta</h3>

            {/* Summary mini-table */}
            <div className={styles.summaryTable}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Paciente</span>
                <span className={styles.summaryValue}>{patientName}</span>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Duración</span>
                <span className={styles.summaryValue}>{duration}</span>
              </div>
            </div>

            {/* Disposition */}
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Disposición <span className={styles.required}>*</span>
              </label>
              <div className={styles.cardGrid}>
                {DISPOSITION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.choiceCard} ${disposicion === opt.value ? styles.choiceCardActive : ''}`}
                    onClick={() => setDisposicion(opt.value)}
                  >
                    <span className={styles.choiceCardLabel}>{opt.label}</span>
                    <span className={styles.choiceCardSub}>{opt.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Diagnóstico principal */}
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="dx-desc">
                Diagnóstico principal <span className={styles.optional}>(opcional)</span>
              </label>
              <div className={styles.dxRow}>
                <input
                  id="dx-cod"
                  className={`${styles.input} ${styles.inputDxCode}`}
                  placeholder="CIE-10"
                  value={dxCodigo}
                  onChange={(e) => setDxCodigo(e.target.value.toUpperCase().slice(0, 12))}
                  aria-label="Código CIE-10"
                />
                <input
                  id="dx-desc"
                  className={`${styles.input} ${styles.inputDxDesc}`}
                  placeholder="Descripción del diagnóstico"
                  value={dxDescripcion}
                  onChange={(e) => setDxDescripcion(e.target.value)}
                />
              </div>
            </div>

            {/* Plan de seguimiento */}
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                <CalendarClock size={13} aria-hidden="true" /> Plan de seguimiento
              </label>
              <div className={styles.planGrid}>
                {PLAN_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.planChip} ${planSeguimiento === opt.value ? styles.planChipActive : ''}`}
                    onClick={() => setPlanSeguimiento(opt.value)}
                  >
                    <span className={styles.planChipLabel}>{opt.label}</span>
                    <span className={styles.planChipSub}>{opt.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Acciones realizadas (toggles) */}
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Acciones realizadas</label>
              <div className={styles.toggleGroup}>
                <button
                  type="button"
                  className={`${styles.toggleCard} ${recetaEmitida ? styles.toggleCardActive : ''}`}
                  onClick={() => setRecetaEmitida((v) => !v)}
                  aria-pressed={recetaEmitida}
                >
                  <Pill size={14} aria-hidden="true" />
                  <span>Receta emitida</span>
                  {recetaEmitida && <CheckCircle2 size={12} aria-hidden="true" className={styles.toggleCheck} />}
                </button>
                <button
                  type="button"
                  className={`${styles.toggleCard} ${ordenLabEmitida ? styles.toggleCardActive : ''}`}
                  onClick={() => setOrdenLabEmitida((v) => !v)}
                  aria-pressed={ordenLabEmitida}
                >
                  <Microscope size={14} aria-hidden="true" />
                  <span>Orden de laboratorio</span>
                  {ordenLabEmitida && <CheckCircle2 size={12} aria-hidden="true" className={styles.toggleCheck} />}
                </button>
                <button
                  type="button"
                  className={`${styles.toggleCard} ${educacionEntregada ? styles.toggleCardActive : ''}`}
                  onClick={() => setEducacionEntregada((v) => !v)}
                  aria-pressed={educacionEntregada}
                >
                  <BookOpen size={14} aria-hidden="true" />
                  <span>Educación entregada</span>
                  {educacionEntregada && <CheckCircle2 size={12} aria-hidden="true" className={styles.toggleCheck} />}
                </button>
              </div>
            </div>

            <div className={styles.actions}>
              <Button variant="ghost" onClick={onClose}>Continuar consulta</Button>
              <Button variant="primary" onClick={() => setStep('sign')}>
                Siguiente
              </Button>
            </div>
          </>
        )}

        {/* ── Step 2: Sign ────────────────────────────────────────────── */}
        {step === 'sign' && (
          <>
            <div className={styles.iconWrap}>
              <div className={`${styles.iconCircle} ${styles.iconCircleAccent}`}>
                <FileSignature size={24} aria-hidden="true" />
              </div>
            </div>

            <h3 className={styles.heading}>Firmar y finalizar</h3>
            <p className={styles.subheading}>
              Revise el resumen y agregue un comentario si lo desea. Al firmar, el
              expediente queda sellado.
            </p>

            {/* Resumen final */}
            <div className={styles.summaryTable}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Disposición</span>
                <span className={styles.summaryValue}>{dispositionLabel}</span>
              </div>
              {(dxCodigo || dxDescripcion) && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Diagnóstico</span>
                  <span className={styles.summaryValue}>
                    {dxCodigo && <code className={styles.code}>{dxCodigo}</code>} {dxDescripcion}
                  </span>
                </div>
              )}
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Seguimiento</span>
                <span className={styles.summaryValue}>
                  {PLAN_OPTIONS.find((o) => o.value === planSeguimiento)?.label}
                </span>
              </div>
              {(recetaEmitida || ordenLabEmitida || educacionEntregada) && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Acciones</span>
                  <span className={styles.summaryValue}>
                    {[
                      recetaEmitida && 'Receta',
                      ordenLabEmitida && 'Orden lab',
                      educacionEntregada && 'Educación',
                    ].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}
            </div>

            {/* SOAP preview */}
            {hasSOAP ? (
              <div className={styles.soapPreview}>
                <p className={styles.soapPreviewTitle}>
                  <Brain size={13} aria-hidden="true" /> Nota SOAP generada
                </p>
                {soapPreview?.s && <p className={styles.soapPreviewLine}><strong>S:</strong> {truncate(soapPreview.s, 140)}</p>}
                {soapPreview?.o && <p className={styles.soapPreviewLine}><strong>O:</strong> {truncate(soapPreview.o, 140)}</p>}
                {soapPreview?.a && <p className={styles.soapPreviewLine}><strong>A:</strong> {truncate(soapPreview.a, 140)}</p>}
                {soapPreview?.p && <p className={styles.soapPreviewLine}><strong>P:</strong> {truncate(soapPreview.p, 140)}</p>}
                <p className={styles.soapPreviewMeta}>
                  {fhirCount > 0 && `${fhirCount} hallazgo${fhirCount === 1 ? '' : 's'} clínico${fhirCount === 1 ? '' : 's'}`}
                  {alertCount > 0 && ` · ${alertCount} alerta${alertCount === 1 ? '' : 's'}`}
                </p>
              </div>
            ) : (disposicion === 'completada' || disposicion === 'referida' || disposicion === 'incompleta') ? (
              // Issue #39: firmar como "Completada"/"Referida" sin nota clínica
              // notifica al paciente como si hubiera recibido atención real —
              // con un solo clic se corrige la disposición a "Fallo técnico"
              // (no genera ese aviso) antes de que el médico firme por error.
              <div className={styles.soapEmptyWarning}>
                <AlertCircle size={14} aria-hidden="true" />
                <div>
                  <p className={styles.soapEmptyWarningTitle}>
                    No se generó ninguna nota clínica
                  </p>
                  <p className={styles.soapEmptyWarningText}>
                    Firmar como «{dispositionLabel}» notificará al paciente que recibió
                    atención. Si la consulta no llegó a realizarse (falla técnica, IA no
                    disponible), cambie la disposición para evitar ese aviso incorrecto.
                  </p>
                  <button
                    type="button"
                    className={styles.soapEmptyWarningAction}
                    onClick={() => setDisposicion('fallo_tecnico')}
                  >
                    Cambiar a «Fallo técnico»
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.soapEmpty}>
                <AlertCircle size={14} aria-hidden="true" />
                <span>No se generó una nota SOAP. ¿Desea finalizar sin nota clínica?</span>
              </div>
            )}

            {/* Comentario libre */}
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="comentario">
                Comentario del médico <span className={styles.optional}>(opcional)</span>
              </label>
              <textarea
                id="comentario"
                className={styles.textarea}
                placeholder="Notas adicionales, instrucciones especiales, próximos pasos..."
                value={comentarioMedico}
                onChange={(e) => setComentarioMedico(e.target.value.slice(0, 2000))}
                rows={3}
              />
              <p className={styles.charCount}>{comentarioMedico.length} / 2000</p>
            </div>

            <div className={styles.actions}>
              <Button variant="ghost" onClick={() => setStep('outcome')}>
                <ChevronLeft size={14} aria-hidden="true" /> Atrás
              </Button>
              <Button variant="primary" onClick={handleConfirm} loading={loading}>
                <PhoneOff size={14} aria-hidden="true" /> Firmar y finalizar
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}
